import { gql } from '@apollo/client';

import { PRICING_TARIFF_FIELDS } from '@/graphql/fragments/pricing_tariffs';
import { PRICING_OVERRIDE_FIELDS } from '@/graphql/fragments/pricing_overrides';
import { RATE_FIELDS } from '@/graphql/fragments/rates';
import { SERVICE_LEVEL_FIELDS } from '@/graphql/fragments/service_levels';
import { SUBREGION_BASE_FIELDS } from '@/graphql/queries/subregions';
import { DRIVER_TARIFF_FIELDS } from '@/graphql/fragments/driver_tariffs';
import { TARIFF_CATEGORIZATION_FIELDS } from '@/components/Account/TariffLibrary/graphql';

export const CREATE_TARIFF = gql`
    ${PRICING_TARIFF_FIELDS}

    mutation CreateTariff($tariffs: [pricing_tariffs_insert_input!]!) {
        tariffs: insert_pricing_tariffs(objects: $tariffs) {
            returning {
                ...PricingTariffFields
            }
        }
    }
`;

export const DELETE_TARIFF = gql`
    ${PRICING_TARIFF_FIELDS}

    mutation DeleteTariff($tariff_id: uuid!) {
        deleted: delete_pricing_tariffs_by_pk(tariff_id: $tariff_id) {
            tariff_id
        }
    }
`;

export const UPSERT_RATES = gql`
    ${RATE_FIELDS}

    mutation UpsertRates($rates: [rates_insert_input!]!, $tariff_id: uuid!, $update: pricing_tariffs_set_input = {}) {
        tariff: update_pricing_tariffs_by_pk(pk_columns: { tariff_id: $tariff_id }, _set: $update) {
            tariff_id
            name
        }

        stale: delete_rates(where: { tariff_id: { _eq: $tariff_id } }) {
            affected_rows
        }

        updated: insert_rates(objects: $rates) {
            ...RateFields
        }
    }
`;

export const UPDATE_PRICING_OVERRIDES = gql`
    ${PRICING_OVERRIDE_FIELDS}

    mutation UpdatePricingOverride($ids: [uuid!]!, $update: pricing_overrides_set_input!) {
        updated: update_pricing_overrides(where: { pricing_override_id: { _in: $ids } }, _set: $update) {
            returning {
                ...PricingOverrideFields
            }
        }
    }
`;

export const UPSERT_PRICING_OVERRIDES = gql`
    ${PRICING_OVERRIDE_FIELDS}

    mutation UpsertPricingOverrides($override: upsert_pricing_overrides_default_args!) {
        updated: upsert_pricing_overrides_default(args: $override) {
            ...PricingOverrideFields
        }
    }
`;
export const DELETE_PRICING_OVERRIDE = gql`
    mutation DeletePricingOverride($pricing_override_id: uuid!) {
        deleted: delete_pricing_overrides_by_pk(pricing_override_id: $pricing_override_id) {
            pricing_override_id
        }
    }
`;

export const UPSERT_DRIVER_TARIFFS = gql`
    ${DRIVER_TARIFF_FIELDS}

    mutation UpsertDriverTariffs($mappings: [driver_tariff_mappings_insert_input!]!) {
        updated: insert_driver_tariff_mappings(
            objects: $mappings
            on_conflict: { constraint: driver_tariff_mappings_pkey, update_columns: [tariff_id] }
        ) {
            returning {
                ...DriverTariffFields
            }
        }
    }
`;

export const DUPLICATE_PRICING_TARIFF = gql`
    ${PRICING_TARIFF_FIELDS}

    mutation DuplicatePricingTariff($tariff_id: uuid!) {
        duplicated: duplicate_pricing_tariff(args: { target_id: $tariff_id }) {
            ...PricingTariffFields
        }
    }
`;

export const LIST_TARIFFS_BY_CLIENT_ID = gql`
    ${PRICING_TARIFF_FIELDS}
    ${PRICING_OVERRIDE_FIELDS}
    ${RATE_FIELDS}
    ${DRIVER_TARIFF_FIELDS}
    ${TARIFF_CATEGORIZATION_FIELDS}

    query ListTariffsByClientId($client_ids: [uuid!]!) {
        tariffs: pricing_tariffs(where: { client_id: { _in: $client_ids } }, order_by: { created_at: desc }) {
            ...PricingTariffFields
            ...TariffCategorizationFields

            rates {
                ...RateFields
            }
        }

        overrides: pricing_overrides(where: { _and: [{ oms: { _eq: true } }, { client_id: { _in: $client_ids } }] }) {
            ...PricingOverrideFields
        }

        driver_tariffs: driver_tariff_mappings(where: { client_id: { _in: $client_ids } }) {
            ...DriverTariffFields
        }
    }
`;

/** The tariff owner's QBO codes and custom service levels. The editor offers these, not the logged-in account's. */
export const QUERY_TARIFF_CLIENT = gql`
    ${SERVICE_LEVEL_FIELDS}

    query TariffClient($client_id: uuid!) {
        client: clients_by_pk(client_id: $client_id) {
            client_id
            qbo_tags
            service_levels(order_by: { service_level: asc }) {
                ...ServiceLevelFields
            }
        }
    }
`;

export const QUERY_TARIFF_RATE_CLASSES = gql`
    query TariffRateClasses($client_id: uuid!) {
        item_dimensions(
            where: { client_id: { _eq: $client_id }, rate_class: { _is_null: false } }
            distinct_on: rate_class
            order_by: { rate_class: asc }
        ) {
            rate_class
        }
    }
`;

export const GET_TARIFFS_BY_TARIFF_ID = gql`
    ${PRICING_TARIFF_FIELDS}
    ${RATE_FIELDS}

    query GetTariffsById($tariff_id: uuid!) {
        result: pricing_tariffs_by_pk(tariff_id: $tariff_id) {
            ...PricingTariffFields

            rates {
                ...RateFields
            }

            requirements {
                rate_type
            }
        }
    }
`;
